//!@TODO: Get it from a lib when it's available
/**
 * Apply a fuzzy normalization to a string, in order to get it to match other fuzzy normalized strings better
 *
 * Currently this does:
 * - fold to lower case
 * - apply unicode decomposition normalization
 * - remove diacritics
 * - fold separators, punctuation and symbols to spaces
 * - contract spaces
 * - normalize unicode
 */
export function fuzzyNormalize(str: string): string {
	return str
		.toLocaleLowerCase()
		.normalize('NFKD')
		.replace(/\p{Diacritic}/giu, '')
		.replace(/\p{Separator}|\p{Punctuation}|\p{Symbol}/giu, ' ')
		.replace(/\s+/, ' ')
		.normalize('NFC')
}

type ArrayLike<T> = { length: number; [_k: number]: T }

/**
 * Options for doing a levenshtein distance calculation
 *
 * @see {@link levenshtein}
 */
export type LevenshteinOptions<T> = {
	/**
	 * How much is an insertion worth
	 *
	 * @defaultValue 1
	 */
	insertionCost?: number
	/**
	 * How much is a deletion worth
	 *
	 * @defaultValue 1
	 */
	deletionCost?: number
	/**
	 * How much is a substitution worth
	 *
	 * @defaultValue 1
	 */
	substitutionCost?: number
	/**
	 * How to compare two elements for equalty
	 *
	 * @defaultValue ===
	 */
	eq?: (_a: T, _b: T) => boolean
}

/**
 * Compute the levenshtein distance on two array or array-like objects (like strings)
 *
 * @see {@link https://en.wikipedia.org/wiki/Levenshtein_distance}
 */
export function levenshtein<T>(
	a: ArrayLike<T>,
	b: ArrayLike<T>,
	opts?: LevenshteinOptions<T>,
): number {
	// https://en.wikipedia.org/wiki/Levenshtein_distance#Iterative_with_two_matrix_rows

	const {
		insertionCost = 1,
		deletionCost = 1,
		substitutionCost = 1,
		eq = (a: T, b: T) => a === b,
	} = opts ?? {}

	if (a === b) return 0

	const m = a.length
	const n = b.length

	let v0 = Array.from({ length: n + 1 }, () => 0)
	let v1 = Array.from({ length: n + 1 }, () => 0)

	for (let i = 0; i <= n; i++) {
		v0[i] = i
	}

	for (let i = 0; i < m; i++) {
		v1[0] = (i + 1) * deletionCost

		for (let j = 0; j < n; j++) {
			const dC = v0[j + 1] + deletionCost
			const iC = v1[j] + insertionCost
			const sC = v0[j] + (eq(a[i], b[j]) ? 0 : substitutionCost)
			v1[j + 1] = Math.min(dC, iC, sC)
		}

		const t = v1
		v0 = v1
		v1 = t
	}

	return v0[n]
}

/**
 * Fuzzy-normalize strings and compute the levenshtein distance between them
 *
 * @see {@link levenshtein}
 */
export function fuzzyLevenshtein(a: string, b: string): number {
	return levenshtein(fuzzyNormalize(a), fuzzyNormalize(b))
}

/**
 * Checks the levenshtein distance of the needle in the haystack
 *
 * @see {@link https://web.archive.org/web/20200809192640/http://ginstrom.com/scribbles/2007/12/01/fuzzy-substring-matching-with-levenshtein-distance-in-python/}
 */
export function fuzzySubstring(needle: string, haystack: string): number {
	needle = fuzzyNormalize(needle)
	haystack = fuzzyNormalize(haystack)

	if (haystack === needle) return 0

	// https://web.archive.org/web/20200809192640/http://ginstrom.com/scribbles/2007/12/01/fuzzy-substring-matching-with-levenshtein-distance-in-python/
	const m = needle.length
	const n = haystack.length

	if (m === 1) return +!haystack.includes(needle)
	if (!n) return m

	let row1 = Array.from({ length: n + 1 }, () => 0)
	for (let i = 0; i < m; i++) {
		const row2 = [i + 1]
		for (let j = 0; j < n; j++) {
			const cost = +(needle[i] !== haystack[j])
			row2.push(
				Math.min(
					row1[j + 1] + 1, // delete
					row2[j] + 1, // insert
					row1[j] + cost, // replace
				),
			)
		}
		row1 = row2
	}
	// eslint-disable-next-line unicorn/no-array-reduce
	return row1.reduce((a, b) => Math.min(a, b))

	//TODO: figure out where we found the match
}

/**
 * Checks if the haystack probably includes the needle with a fuzzy levenshtein distance
 *
 * @see {@link fuzzySubstring}
 */
export function fuzzyIncludes(needle: string, haystack: string): boolean {
	needle = fuzzyNormalize(needle)
	haystack = fuzzyNormalize(haystack)

	const distance = fuzzySubstring(needle, haystack)
	return distance < 1 + Math.floor(needle.length / 5)
}

//TODO: implement this https://hal.archives-ouvertes.fr/hal-01360482/file/LATA2016.pdf
// this would basically be a full-text fuzzy search
